/**
 * The different phrasings browsers use for a single dynamic-import / code-split
 * chunk-load failure.
 *
 * Apps lazy-load routes via `import(...)`. When a new version is deployed the
 * content-hashed chunk files referenced by an already-open tab are replaced, so
 * navigating to a not-yet-loaded route fails to import and the browser throws —
 * most commonly a `TypeError: "Failed to fetch dynamically imported module: ..."`.
 * Reloading fetches the fresh entry point and its current chunks.
 *
 * These are transient, expected failures around a deploy rather than bugs, so
 * apps use this to recover gracefully (a refresh prompt or auto-reload) and to
 * filter the noise out of Sentry. The list must cover every browser's phrasing:
 * before the Vite migration only the Webpack messages existed, and the native
 * browser messages slip past a `name === "ChunkLoadError"` check
 * (WATCHTOWER-220 / GAS-988, KRS-990).
 */
export const CHUNK_LOAD_ERROR_MESSAGE_PATTERNS: readonly RegExp[] = [
    // Webpack (pre-Vite build).
    /Loading chunk [\s\S]*? failed/i,
    /Loading CSS chunk [\s\S]*? failed/i,
    // Chromium: Chrome, Edge, Android WebView.
    /Failed to fetch dynamically imported module/i,
    // Firefox.
    /error loading dynamically imported module/i,
    // Safari / iOS.
    /Importing a module script failed/i,
    // Vite preload helper (CSS).
    /Unable to preload CSS/i,
];

/**
 * Determine whether an error represents a failed dynamic import / chunk load,
 * across every browser's phrasing of the failure.
 *
 * @param error The error thrown by the router or runtime.
 * @returns True if the error is a chunk load error, otherwise false.
 */
export const isChunkLoadError = (error: unknown): boolean => {
    if (typeof error !== "object" || error === null) {
        return false;
    }

    // Browser errors vary in shape (native TypeError, webpack ChunkLoadError, etc.), so narrow the unknown object
    // structurally to the two fields we read rather than relying on `instanceof Error`.
    const { name, message } = error as { name?: unknown; message?: unknown };

    if (name === "ChunkLoadError") {
        return true;
    }

    return typeof message === "string" && CHUNK_LOAD_ERROR_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
};
